import { Descriptions as AntdDescriptions } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import GeneralNotificationContext from "../../../context/general-notification/context";
import GeneralNotificationContextProvider from "../../../context/general-notification/provider";
import Tag from "../../general/antd/tag";
import {
  getNotificationColor,
  getNotificationType,
  NotificationType,
} from "../../../models/general-notification/enum";
import EndPoints from "../../../services/end-points";

interface Props { }

const GeneralNotification: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(GeneralNotificationContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [itemName, setItemName] = useState<string>("-");
  const [itemLoading, setItemLoading] = useState<boolean>(false);

  // Fetch the associated item details based on type and itemId
  const fetchItemDetails = async () => {
    if (!details?.type || !details?.itemId) return;
    
    setItemLoading(true);
    try {
      switch (details.type) {
        case NotificationType.CAMPAIGN:
          const campaignResponse = await EndPoints.campaign.getCampaign(details.itemId);
          setItemName(campaignResponse.data?.name || "-");
          break;
        case NotificationType.CHARITY:
          const charityResponse = await EndPoints.charity.getCharity(details.itemId);
          setItemName(charityResponse.data?.name || "-");
          break;
        case NotificationType.ORPHAN:
          const orphanResponse = await EndPoints.orphan.getOrphan(details.itemId);
          setItemName(`${orphanResponse.data?.firstName} ${orphanResponse.data?.lastName}` || "-");
          break;
        default:
          setItemName("-");
      }
    } catch (error) {
      console.error("Error fetching item details:", error);
      setItemName("-");
    } finally {
      setItemLoading(false);
    }
  };

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);

  useEffect(() => {
    if (details) {
      fetchItemDetails();
    }
  }, [details]);

  // Get the label for the associated item based on type
  const getItemLabel = () => {
    if (!details?.type) return t("associated_item");
    
    switch (details.type) {
      case NotificationType.CAMPAIGN:
        return t("campaign");
      case NotificationType.CHARITY:
        return t("charity");
      case NotificationType.ORPHAN:
        return t("orphan");
      default:
        return t("associated_item");
    }
  };

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("general_notification")}
        buttonName={`${t("update_general_notification")}`}
        onClick={() => navigate(`/general-notification/update/${id}`)}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("title")}>
              {details?.title ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("user_slide")}>
              <Tag
                title={t(details?.topic)}
                color={getNotificationColor(details?.type)}
              />
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("type")}>
              {details?.type ? t(getNotificationType(details.type)) : "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={getItemLabel()}>
              {itemLoading ? <Spin size="small" /> : itemName}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("text")}>
              {details?.body ?? "-"}
            </AntdDescriptions.Item>
            {/*<AntdDescriptions.Item label={t("note")}>*/}
            {/*  {details?.note ?? "-"}*/}
            {/*</AntdDescriptions.Item>*/}
            {/*<AntdDescriptions.Item label={t("product")}>*/}
            {/*  {details?.campaign?.name ?? "-"}*/}
            {/*</AntdDescriptions.Item>*/}
            {/* to do from back */}

            {/* <AntdDescriptions.Item label={t("note_ar")}>
              {details?.translations?.note[0]?.value ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("note_en")}>
              {details?.translations?.note[1]?.value ?? "-"}
            </AntdDescriptions.Item> */}
          </Descriptions>
        )}
      </PageDetails>
    </>
  );
};

const GeneralNotificationDetailsPage = () => {
  return (
    <GeneralNotificationContextProvider>
      <GeneralNotification />
    </GeneralNotificationContextProvider>
  );
};
export default GeneralNotificationDetailsPage;
